
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { supabase, type SiteSettings } from "@/lib/supabase";
import LoadingLogo from "@/components/LoadingLogo";
import { useState, useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import Layout from "@/components/Layout";
import AdminRoute from "@/components/AdminRoute";

// Importiamo direttamente tutti i componenti
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/admin/Dashboard";
import Clients from "@/pages/admin/Clients";
import ClientDetail from "@/pages/admin/ClientDetail";
import Packages from "@/pages/admin/Packages";
import Courses from "@/pages/admin/Courses";
import Calendar from "@/pages/admin/Calendar";
import Payments from "@/pages/admin/Payments";
import Notifications from "@/pages/admin/Notifications";
import ClientNotifications from "@/pages/client/Notifications";
import HomeOptions from "@/pages/admin/HomeOptions";
import Workouts from "@/pages/admin/Workouts";
import NotFound from "@/pages/NotFound";

// Configurazione del client di React Query con cache persistente
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false, // Disabilitiamo il refresh su focus
      staleTime: 5 * 60 * 1000, // Consideriamo i dati "freschi" per 5 minuti
      gcTime: 10 * 60 * 1000, // Manteniamo i dati in cache per 10 minuti (prima era cacheTime)
    }
  }
});

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);

  // Funzione per precaricare i dati principali
  const preloadData = async () => {
    try {
      // Carica le impostazioni del sito
      const { data: settings } = await supabase
        .from("site_settings")
        .select("*")
        .single();

      if (settings) {
        setSiteSettings(settings);
        
        // Applica le impostazioni del tema
        document.documentElement.style.setProperty('--color-heading', settings.heading_color || '#1A1F2C');
        document.documentElement.style.setProperty('--color-paragraph', settings.paragraph_color || '#4B5563');
        document.documentElement.style.setProperty('--color-button', settings.button_color || '#8B5CF6');
        
        const buttonColorHSL = hexToHSL(settings.button_color || '#8B5CF6');
        document.documentElement.style.setProperty('--color-button-hsl', buttonColorHSL);
        
        if (settings.primary_font) {
          document.documentElement.style.fontFamily = settings.primary_font;
        }
      }

      // Dopo il caricamento di tutti i dati necessari, nascondi il loader
      setTimeout(() => {
        setIsLoading(false);
      }, 1500);

    } catch (error) {
      console.error("Errore nel precaricamento dei dati:", error);
      setIsLoading(false);
    }
  };

  // Effettua il precaricamento dei dati al mount dell'applicazione
  useEffect(() => {
    preloadData();

    // Imposta un intervallo per ricaricare i dati ogni 5 minuti
    const intervalId = setInterval(() => {
      preloadData();
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const hexToHSL = (hex: string): string => {
    hex = hex.replace(/^#/, '');
    
    let r = parseInt(hex.substring(0, 2), 16) / 255;
    let g = parseInt(hex.substring(2, 4), 16) / 255;
    let b = parseInt(hex.substring(4, 6), 16) / 255;
    
    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    
    let l = (max + min) / 2;
    
    let h = 0;
    let s = 0;
    
    if (max !== min) {
      s = l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
      
      if (max === r) {
        h = ((g - b) / (max - min)) + (g < b ? 6 : 0);
      } else if (max === g) {
        h = ((b - r) / (max - min)) + 2;
      } else {
        h = ((r - g) / (max - min)) + 4;
      }
      
      h = Math.round(h * 60);
    }
    
    s = Math.round(s * 100);
    l = Math.round(l * 100);
    
    return `${h} ${s}% ${l}%`;
  };

  if (isLoading) {
    return <LoadingLogo onLoadingComplete={() => setIsLoading(false)} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <TooltipProvider>
            <SidebarProvider>
              <Toaster />
              <Sonner />
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="login" element={<Login />} />
                  <Route path="reset-password" element={<ResetPassword />} />
                  
                  {/* Admin Routes */}
                  <Route path="admin" element={<AdminRoute />}>
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="clients" element={<Clients />} />
                    <Route path="clients/:slug" element={<ClientDetail />} />
                    <Route path="packages" element={<Packages />} />
                    <Route path="courses" element={<Courses />} />
                    <Route path="calendar" element={<Calendar />} />
                    <Route path="workouts" element={<Workouts />} />
                    <Route path="payments" element={<Payments />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="client-notifications" element={<ClientNotifications />} />
                    <Route path="home-options" element={<HomeOptions />} />
                  </Route>
                  
                  {/* Nuove rotte per URL personalizzati con nome-cognome */}
                  <Route path=":userSlug" element={<AdminRoute />}>
                    <Route path="calendar" element={<Calendar />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="clients" element={<Clients />} />
                    <Route path="clients/:slug" element={<ClientDetail />} />
                    <Route path="packages" element={<Packages />} />
                    <Route path="courses" element={<Courses />} />
                    <Route path="workouts" element={<Workouts />} />
                    <Route path="payments" element={<Payments />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="client-notifications" element={<ClientNotifications />} />
                    <Route path="home-options" element={<HomeOptions />} />
                  </Route>
                  
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </SidebarProvider>
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
